import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import PageHero from '../components/PageHero';
import herixonAvatar from '../images/ʜᴇʀɪxᴏɴ075.webp';
import mysterAvatar from '../images/MysterTrolles.webp';

const Friends: React.FC = () => {
  const { t } = useTranslation('friends');
  return (
    <div className="min-h-screen pt-24">
      <section className="page-section">
        <div className="container mx-auto px-6">
          <PageHero title={t('heroTitle')} subtitle={t('intro')} />
        </div>
      </section>

      <section className="page-section pb-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mx-auto max-w-4xl text-center"
          >

            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, staggerChildren: 0.08 } } }}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-6 mb-10 text-left items-start"
            >
              <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.99 }} transition={{ duration: 0.25 }} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }} role="article" tabIndex={0} className="group relative p-6 rounded-2xl glass-effect border border-dark-600 hover:border-primary-500/50 hover:shadow-glow transition-shadow duration-300 overflow-hidden bg-gradient-to-br from-dark-800/40 to-dark-700/30">
                <div className="flex items-center gap-4 mb-3">
                  <motion.img
                    src={herixonAvatar}
                    alt="HeriXon075"
                    loading="lazy"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-offset-2 ring-offset-dark-900 ring-primary-500/40 shadow-glow"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  />
                  <div>
                    <h3 className="text-xl font-display font-semibold gradient-text">HeriXon075</h3>
                    <div className="leading-tight">
                      <div className="text-[11px] uppercase tracking-wide text-dark-400">{t('friendSince')}</div>
                      <motion.div whileHover={{ scale: 1.03 }} className="inline-flex items-center text-primary-400 font-semibold tabular-nums text-sm bg-primary-500/10 ring-1 ring-primary-500/20 rounded-md px-2 py-0.5 transition">25. 8. 2021</motion.div>
                    </div>
                  </div>
                </div>
                <p className="text-dark-200 leading-relaxed">{t('friends.herixon.description','HeriXon075 je můj dlouhodobý kamarád, se kterým se známe přes Discord od roku 2021. Je oporou, na kterou se vždy mohu spolehnout. Zná mé slabosti i radosti a nikdy mě nesoudí. Společně jsme prožili spoustu zážitků, ať už při hrách, online chatování, nebo v těžších chvílích, kdy bylo důležité cítit podporu a porozumění.')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.humor')}</span>
  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.physicalSupport')}</span>
  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.mentalSupport')}</span>
</div>

                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute top-0 bottom-0 w-1/2 -translate-x-1/2 bg-gradient-to-r from-transparent via-primary-500/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100"
                  initial={{ x: -150 }}
                  animate={{ x: 150 }}
                  transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.2 }}
                />
                <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-primary-500/30 rounded-tr-lg group-hover:border-primary-500/60 transition-colors duration-300" />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-secondary-500/30 rounded-bl-lg group-hover:border-secondary-500/60 transition-colors duration-300" />
              </motion.div>
              <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.99 }} transition={{ duration: 0.25 }} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }} role="article" tabIndex={0} className="group relative p-6 rounded-2xl glass-effect border border-dark-600 hover:border-primary-500/50 hover:shadow-glow transition-shadow duration-300 overflow-hidden bg-gradient-to-br from-dark-800/40 to-dark-700/30">
                <div className="flex items-center gap-4 mb-3">
                  <motion.img
                    src={mysterAvatar}
                    alt="MysterTrolles"
                    loading="lazy"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-offset-2 ring-offset-dark-900 ring-secondary-500/40 shadow-glow"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  />
                  <div>
                    <h3 className="text-xl font-display font-semibold gradient-text">MysterTrolles</h3>
                    <div className="leading-tight">
                      <div className="text-[11px] uppercase tracking-wide text-dark-400">{t('friendSince','Přátelé od')}</div>
                      <motion.div whileHover={{ scale: 1.03 }} className="inline-flex items-center text-primary-400 font-semibold tabular-nums text-sm bg-primary-500/10 ring-1 ring-primary-500/20 rounded-md px-2 py-0.5 transition">12. 4. 2023</motion.div>
                    </div>
                  </div>
                </div>
                <p className="text-dark-200 leading-relaxed">{t('friends.myster.description')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.humor')}</span>
  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.physicalSupport')}</span>
  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.mentalSupport')}</span>
</div>

                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute top-0 bottom-0 w-1/2 -translate-x-1/2 bg-gradient-to-r from-transparent via-primary-500/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100"
                  initial={{ x: -150 }}
                  animate={{ x: 150 }}
                  transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.2 }}
                />
                <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-primary-500/30 rounded-tr-lg group-hover:border-primary-500/60 transition-colors duration-300" />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-primary-500/30 rounded-bl-lg group-hover:border-primary-500/60 transition-colors duration-300" />
              </motion.div>

              <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.99 }} transition={{ duration: 0.25 }} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }} role="article" tabIndex={0} className="group relative p-6 rounded-2xl glass-effect border border-dark-600 hover:border-primary-500/50 hover:shadow-glow transition-shadow duration-300 overflow-hidden bg-gradient-to-br from-dark-800/40 to-dark-700/30">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-xl shadow-glow ring-2 ring-offset-2 ring-offset-dark-900 ring-primary-500/40">
                    S
                  </div>
                  <div>
                    <h3 className="text-xl font-display font-semibold gradient-text">satorugojo_yowaimo</h3>
                    <div className="leading-tight">
                      <div className="text-[11px] uppercase tracking-wide text-dark-400">{t('friendSince','Přátelé od')}</div>
                      <motion.div whileHover={{ scale: 1.03 }} className="inline-flex items-center text-primary-400 font-semibold tabular-nums text-sm bg-primary-500/10 ring-1 ring-primary-500/20 rounded-md px-2 py-0.5 transition">15. 1. 2024</motion.div>
                    </div>
                  </div>
                </div>
                <p className="text-dark-200 leading-relaxed">{t('friends.gojo.description')}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.humor')}</span>
                  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.energy')}</span>
                  <span className="px-2 py-1 bg-dark-700/50 rounded text-xs text-dark-300 border border-dark-600">{t('tags.support')}</span>
                </div>

                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute top-0 bottom-0 w-1/2 -translate-x-1/2 bg-gradient-to-r from-transparent via-primary-500/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100"
                  initial={{ x: -150 }}
                  animate={{ x: 150 }}
                  transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.2 }}
                />
                <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-primary-500/30 rounded-tr-lg group-hover:border-primary-500/60 transition-colors duration-300" />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-secondary-500/30 rounded-bl-lg group-hover:border-secondary-500/60 transition-colors duration-300" />
              </motion.div>

            </motion.div>
            <p className="text-lg text-dark-300 mb-10 text-left">
              {t('communityDescription','Společně tvoří moji malou komunitu, kde panuje důvěra, porozumění a společná radost. Díky nim se necítím sám a každý den mám pocit, že patřím někam, kde mohu být autentický a být s lidmi, kteří mě podporují.')}
            </p>

            <div className="p-8 rounded-3xl glass-effect border border-primary-500/20">
              <div className="text-6xl mb-4">🤝</div>
              <h2 className="text-2xl font-semibold text-primary-400 mb-3">{t('communityTitle','Spolu tvoříme víc')}</h2>
              <p className="text-dark-200 leading-relaxed">
                {t('discordDescription','Discord zatím nemáme veřejně dostupný. Připravujeme komunitní server — brzy bude!')}
              </p>
              <button
                type="button"
                aria-disabled="true"
                className="inline-flex items-center mt-6 px-6 py-3 bg-gradient-to-r from-dark-600 to-dark-700 text-dark-300 rounded-xl font-semibold shadow-lg opacity-60 cursor-not-allowed pointer-events-none"
              >
                {t('discordSoon','💬 Discord — brzy')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Friends;
